<script setup lang="ts">

import {useMainStore} from "@/stores/main";
import {useRequestStore} from "@/stores/request";
import {useLocaleStore} from "@/stores/l10n";
import Loading from "@/components/atoms/Loading.vue";
import Input from "@/components/Input.vue";
import Button from "@/components/Button.vue";
import Textarea from "@/components/Textarea.vue";
import DatePicker from "@/components/DatePicker.vue";
import Select from "@/components/Select.vue";
import {computed, watch} from "vue";
import Radio from "@/components/Radio.vue";
import BranchInfo from "@/components/BranchInfo.vue";
import Items from "@/components/Items.vue";

const mainStore = useMainStore()

const requestStore = useRequestStore()
const localeStore = useLocaleStore()

const returnMethodOptions = computed(() => [
    { value: 'store', label: localeStore.getText('withdrawal_return_store') },
    { value: 'courier', label: localeStore.getText('withdrawal_return_courier') },
    { value: 'other', label: localeStore.getText('withdrawal_return_other') },
])

const isPartialOptions = computed(() => [
    { value: false, label: localeStore.getText('withdrawal_not_partial') },
    { value: true, label: localeStore.getText('withdrawal_partial') },
])

watch(() => requestStore.form.is_partial, (val) => {
        if (!val) {
            requestStore.form.items = [];
        }
    }
)

</script>

<template>

    <div v-if="mainStore.loading">
        <Loading></Loading>
    </div>

    <template v-else-if="mainStore.complete">
        <div class="grid grid-cols-1 gap-8 justify-items-center">
            <div class="inline-grid items-center justify-center rounded-full w-[150px] h-[150px] p-10 bg-green-100">
                <img src="./../public/icons/success.svg" class="w-[100px]" alt="">
            </div>

            <h2 class="font-bold text-xl">{{ localeStore.getText('withdrawal_success_title') }}</h2>
            <p>{{ localeStore.getText('withdrawal_success_description') }}</p>
        </div>
    </template>

    <template v-else>
        <div v-if="mainStore.loading">
            <Loading></Loading>
        </div>

        <div v-else class="grid grid-cols-1 gap-8">
            <div class="grid grid-cols-1 gap-2 text-center">
                <h1 class="text-center text-2xl font-bold">{{ localeStore.getText('withdrawal_form_title') }}</h1>
                <p>{{ localeStore.getText('withdrawal_form_description') }}</p>
                <p class="font-bold">{{ localeStore.getText('withdrawal_store_address') }}</p>
            </div>

            <div class="grid grid-cols-1 gap-4">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input id="full_name" required :label="localeStore.getText('withdrawal_full_name')" v-model="requestStore.form.full_name" :disabled="requestStore.loading" :error="requestStore.errors?.full_name?.[0]" @clear-error="requestStore.clearError('full_name')"></Input>
                    <Input id="order_id" required :label="localeStore.getText('withdrawal_order_id')" v-model="requestStore.form.order_id" :disabled="requestStore.loading" :error="requestStore.errors?.order_id?.[0]" @clear-error="requestStore.clearError('order_id')"></Input>
                    <Input id="address_street" required :label="localeStore.getText('withdrawal_address_street')" v-model="requestStore.form.address.street" :disabled="requestStore.loading" :error="requestStore.errors?.['address.street']?.[0]" @clear-error="requestStore.clearError('address.street')"></Input>
                    <Input id="email" required :label="localeStore.getText('withdrawal_email')" v-model="requestStore.form.email" :disabled="requestStore.loading" :error="requestStore.errors?.email?.[0]" @clear-error="requestStore.clearError('email')"></Input>
                    <Input id="address_city" required :label="localeStore.getText('withdrawal_address_city')" v-model="requestStore.form.address.city" :disabled="requestStore.loading" :error="requestStore.errors?.['address.city']?.[0]" @clear-error="requestStore.clearError('address.city')"></Input>
                    <Input id="phone" :label="localeStore.getText('withdrawal_phone')" v-model="requestStore.form.phone" :disabled="requestStore.loading" :error="requestStore.errors?.phone?.[0]" @clear-error="requestStore.clearError('phone')"></Input>
                    <Input id="address_zip" required :label="localeStore.getText('withdrawal_address_zip')" v-model="requestStore.form.address.zip" :disabled="requestStore.loading" :error="requestStore.errors?.['address.zip']?.[0]" @clear-error="requestStore.clearError('address.zip')"></Input>
                    <Input id="iban" required :label="localeStore.getText('withdrawal_iban')" v-model="requestStore.form.iban" :disabled="requestStore.loading" :error="requestStore.errors?.iban?.[0]" @clear-error="requestStore.clearError('iban')"></Input>
                    <DatePicker id="order_date" required :label="localeStore.getText('withdrawal_order_date')" v-model="requestStore.form.order_date" :disabled="requestStore.loading" :error="requestStore.errors?.order_date?.[0]" @clear-error="requestStore.clearError('order_date')"></DatePicker>
                    <DatePicker id="delivery_date" required :days-back="14" :label="localeStore.getText('withdrawal_delivery_date')" v-model="requestStore.form.delivery_date" :disabled="requestStore.loading" :error="requestStore.errors?.delivery_date?.[0]" @clear-error="requestStore.clearError('delivery_date')"></DatePicker>
                    <Select id="return_method" required :label="localeStore.getText('withdrawal_return_method')" v-model="requestStore.form.return_method" :disabled="requestStore.loading" :error="requestStore.errors?.return_method?.[0]" :options="returnMethodOptions" @clear-error="requestStore.clearError('return_method')" class="col-span-full"></Select>
                    <Textarea id="return_desc" required :label="localeStore.getText('withdrawal_return_desc')" v-model="requestStore.form.return_method_description" :disabled="requestStore.loading" :error="requestStore.errors?.return_method_description?.[0]" v-if="requestStore.form.return_method === 'other'" @clear-error="requestStore.clearError('return_method_description')" class="col-span-full" :rows="2"></Textarea>
                    <BranchInfo v-if="requestStore.form.return_method !== 'other' && requestStore.form.return_method !== null" class="col-span-full"></BranchInfo>
                    <Radio id="is_partial" required :label="localeStore.getText('withdrawal_is_partial')" v-model="requestStore.form.is_partial" :disabled="requestStore.loading" :error="requestStore.errors?.is_partial?.[0]" :options="isPartialOptions" class="col-span-full" @clear-error="requestStore.clearError('is_partial')"></Radio>
                    <Items v-if="requestStore.form.is_partial" :error="requestStore.errors?.['items']?.[0]" class="col-span-full"></Items>
                    <Textarea id="description" required :label="localeStore.getText('withdrawal_description')" v-model="requestStore.form.description" :disabled="requestStore.loading" class="col-span-full" :rows="5" :error="requestStore.errors?.description?.[0]" @clear-error="requestStore.clearError('description')"></Textarea>
                </div>

                <div>
                    <h3>{{ localeStore.getText('withdrawal_instructions_title') }}</h3>
                    <div v-html="localeStore.getText('withdrawal_instructions_content')"></div>
                </div>

                <Button :loading="requestStore.loading" @click="requestStore.submit">
                    {{ localeStore.getText('withdrawal_submit') }}
                </Button>
            </div>
        </div>
    </template>

</template>
